import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getLoansReports,
  clearError,
  clearReports,
} from "../../../../redux/Slices/authSlice";
import { FaCalendarAlt, FaFileExcel, FaPrint, FaEye } from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useTitle } from "../../../../context/TitleContext";
import PdfPreviewModal from "../../Accounting/Reports/components/PdfPreviewModal";
import SortOrderSelect from "../../../../shared/components/SortOrderSelect";
import {
  chunkArray,
  convertArabicNumbersToEnglish,
} from "../../../../shared/utils.js/utils";

const LoansReport = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { loansReports, isLoading, error } = useSelector(state => state.auth);
  const { t, i18n } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";
  const locale = "en-US";
  const [reportDate, setReportDate] = useState("");
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [allReports, setAllReports] = useState([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [isFetchingReport, setIsFetchingReport] = useState(false);
  const [pdfChunks, setPdfChunks] = useState([]);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState("");
  const [deptFilter, setDeptFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("employee_name");
  const [sortOrder, setSortOrder] = useState("asc");

  useEffect(() => {
    setTitle("sidenav.reports.loans");
  }, [setTitle]);

  useEffect(() => {
    return () => {
      setReportDate("");
      setAllReports([]);
      setHasGenerated(false);
      dispatch(clearReports());
    };
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  // Update allReports when loansReports changes (only after user generates report and not while fetching)
  useEffect(() => {
    if (!hasGenerated || isFetchingReport) return;
    const reportsData = loansReports?.data || loansReports || [];
    if (Array.isArray(reportsData) && reportsData.length > 0) {
      setAllReports(reportsData);
      setCurrentPage(1);
    } else {
      setAllReports([]);
    }
  }, [loansReports, hasGenerated, isFetchingReport]);

  const handleGenerateReport = async () => {
    if (!reportDate) {
      toast.error(
        t("reports.workingHours.selectMonth", {
          defaultValue: "Please select a month",
        }),
        { rtl: isRTL },
      );
      return;
    }
    const [year, month] = reportDate.split("-");
    setAllReports([]);
    setHasGenerated(true);
    setIsFetchingReport(true);
    dispatch(clearReports());
    try {
      const result = await dispatch(
        getLoansReports({ year: year, month: month }),
      ).unwrap();
      const reportsData = result?.data || result || [];
      setAllReports(Array.isArray(reportsData) ? reportsData : []);
      setCurrentPage(1);
    } catch (err) {
      setAllReports([]);
    } finally {
      setIsFetchingReport(false);
    }
  };

  // Reset data when date changes
  useEffect(() => {
    setAllReports([]);
    setHasGenerated(false);
    dispatch(clearReports());
  }, [reportDate, dispatch]);

  const handleApplyPerPage = () => {
    const validValue = Math.max(
      1,
      Math.min(perPageInput, filteredAndSortedReports?.length || 1000),
    );
    setPerPage(validValue);
    setCurrentPage(1);
  };

  const getRemainingAmount = item => {
    const remaining =
      item?.remaining_amount ??
      item?.remaining_balance ??
      item?.remaining ??
      null;
    if (remaining !== null && remaining !== undefined && remaining !== "") {
      const value = Number(remaining);
      return Number.isNaN(value) ? 0 : value;
    }
    if (item?.amount !== undefined && item?.paid_amount !== undefined) {
      const value = Number(item.amount) - Number(item.paid_amount);
      return Number.isNaN(value) ? 0 : value;
    }
    return 0;
  };

  const getPaidAmount = item => {
    const paid = item?.paid_amount ?? item?.paid ?? item?.paid_value ?? null;
    if (paid !== null && paid !== undefined && paid !== "") {
      const value = Number(paid);
      return Number.isNaN(value) ? 0 : value;
    }
    const remaining =
      item?.remaining_amount ??
      item?.remaining_balance ??
      item?.remaining ??
      null;
    if (remaining !== null && remaining !== undefined && remaining !== "") {
      const value = Number(item?.amount || 0) - Number(remaining);
      return Number.isNaN(value) ? 0 : Math.max(0, value);
    }
    return 0;
  };

  // Unique departments computed from reports data
  const uniqueDepartments = React.useMemo(() => {
    const list = allReports
      .map(
        item =>
          item.department ||
          (item.employee?.department
            ? (isRTL
                ? item.employee.department.name_ar
                : item.employee.department.name_en) ||
              item.employee.department.name
            : ""),
      )
      .filter(Boolean);
    return Array.from(new Set(list));
  }, [allReports, isRTL]);

  // Client-side filtering and sorting
  const filteredAndSortedReports = React.useMemo(() => {
    let result = [...allReports];

    // search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(item => {
        const empName =
          item.employee_name ||
          (item.employee
            ? (isRTL ? item.employee.name_ar : item.employee.name_en) ||
              item.employee.name
            : "");
        return empName.toLowerCase().includes(term);
      });
    }

    // dept filter
    if (deptFilter !== "all") {
      result = result.filter(item => {
        const dept =
          item.department ||
          (item.employee?.department
            ? (isRTL
                ? item.employee.department.name_ar
                : item.employee.department.name_en) ||
              item.employee.department.name
            : "");
        return dept === deptFilter;
      });
    }

    // status filter
    if (statusFilter !== "all") {
      result = result.filter(item => {
        const status = item.status || "";
        return status === statusFilter;
      });
    }

    // sort
    result.sort((a, b) => {
      let aVal = "";
      let bVal = "";

      if (sortField === "employee_name") {
        aVal =
          a.employee_name ||
          (a.employee
            ? (isRTL ? a.employee.name_ar : a.employee.name_en) ||
              a.employee.name
            : "");
        bVal =
          b.employee_name ||
          (b.employee
            ? (isRTL ? b.employee.name_ar : b.employee.name_en) ||
              b.employee.name
            : "");
      } else if (sortField === "department") {
        aVal =
          a.department ||
          (a.employee?.department
            ? (isRTL
                ? a.employee.department.name_ar
                : a.employee.department.name_en) || a.employee.department.name
            : "");
        bVal =
          b.department ||
          (b.employee?.department
            ? (isRTL
                ? b.employee.department.name_ar
                : b.employee.department.name_en) || b.employee.department.name
            : "");
      } else if (sortField === "status") {
        aVal = a.status || "";
        bVal = b.status || "";
      } else if (sortField === "loan_date") {
        aVal = a.loan_date || a.date || a.created_at?.split("T")[0] || "";
        bVal = b.loan_date || b.date || b.created_at?.split("T")[0] || "";
      } else if (sortField === "amount") {
        const aNum = parseFloat(a.amount) || 0;
        const bNum = parseFloat(b.amount) || 0;
        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      } else if (sortField === "paid") {
        const aNum = getPaidAmount(a);
        const bNum = getPaidAmount(b);
        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      } else if (sortField === "remaining") {
        const aNum = getRemainingAmount(a);
        const bNum = getRemainingAmount(b);
        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      } else {
        aVal = a[sortField] || "";
        bVal = b[sortField] || "";
      }

      return sortOrder === "asc"
        ? String(aVal).localeCompare(String(bVal), isRTL ? "ar" : "en")
        : String(bVal).localeCompare(String(aVal), isRTL ? "ar" : "en");
    });

    return result;
  }, [
    allReports,
    searchTerm,
    deptFilter,
    statusFilter,
    sortField,
    sortOrder,
    isRTL,
  ]);

  // Height-based dynamic PDF chunking
  const updatePdfChunks = () => {
    if (!filteredAndSortedReports || filteredAndSortedReports.length === 0) {
      setPdfChunks([]);
      return;
    }

    const container = document.createElement("div");
    container.style.position = "absolute";
    container.style.top = "-99999px";
    container.style.left = "-99999px";
    container.style.width = "794px";
    container.style.visibility = "hidden";
    document.body.appendChild(container);

    const testPage = document.createElement("div");
    testPage.style.width = "794px";
    testPage.style.height = "1123px";
    testPage.style.padding = "40px";
    testPage.style.boxSizing = "border-box";
    testPage.style.border = "5px solid #000";

    const pageBorder = document.createElement("div");
    pageBorder.style.border = "2px solid #000";
    pageBorder.style.height = "1033px";
    pageBorder.style.padding = "18px 18px 10px";
    pageBorder.style.display = "flex";
    pageBorder.style.flexDirection = "column";
    pageBorder.style.boxSizing = "border-box";

    testPage.appendChild(pageBorder);
    container.appendChild(testPage);

    const topHeader = document.getElementById("pdf-measure-top-header");
    let topHeaderHeight = 220;
    if (topHeader) {
      const clonedHeader = topHeader.cloneNode(true);
      pageBorder.appendChild(clonedHeader);
      topHeaderHeight = clonedHeader.offsetHeight;
      pageBorder.removeChild(clonedHeader);
    }

    const tableHeader = document.getElementById("pdf-measure-thead");
    let tableHeaderHeight = 35;
    if (tableHeader) {
      const tableWrapper = document.createElement("table");
      tableWrapper.style.width = "100%";
      tableWrapper.style.borderCollapse = "collapse";
      const clonedTHead = tableHeader.cloneNode(true);
      tableWrapper.appendChild(clonedTHead);
      pageBorder.appendChild(tableWrapper);
      tableHeaderHeight = tableWrapper.offsetHeight;
      pageBorder.removeChild(tableWrapper);
    }

    const footer = document.getElementById("pdf-measure-footer");
    let footerHeight = 70;
    if (footer) {
      const clonedFooter = footer.cloneNode(true);
      pageBorder.appendChild(clonedFooter);
      footerHeight = clonedFooter.offsetHeight;
      pageBorder.removeChild(clonedFooter);
    }

    document.body.removeChild(container);

    const PAGE_MAX_HEIGHT = 1033;
    const availableHeightForTable =
      PAGE_MAX_HEIGHT - topHeaderHeight - footerHeight - 15;

    const tbody = document.getElementById("pdf-measure-tbody");
    let rowHeights = [];
    if (tbody) {
      const rows = tbody.querySelectorAll("tr");
      rowHeights = Array.from(rows).map(row => row.offsetHeight || 30);
    } else {
      rowHeights = filteredAndSortedReports.map(() => 30);
    }

    const chunks = [];
    let currentChunk = [];
    let currentHeight = tableHeaderHeight;

    for (let i = 0; i < filteredAndSortedReports.length; i++) {
      const rHeight = rowHeights[i] || 30;
      if (currentHeight + rHeight > availableHeightForTable) {
        chunks.push(currentChunk);
        currentChunk = [filteredAndSortedReports[i]];
        currentHeight = tableHeaderHeight + rHeight;
      } else {
        currentChunk.push(filteredAndSortedReports[i]);
        currentHeight += rHeight;
      }
    }
    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    setPdfChunks(chunks);
  };

  useEffect(() => {
    if (filteredAndSortedReports && filteredAndSortedReports.length > 0) {
      const timer = setTimeout(() => {
        updatePdfChunks();
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setPdfChunks([]);
    }
  }, [filteredAndSortedReports]);

  // Client-side pagination
  const totalPages = Math.max(
    1,
    Math.ceil(filteredAndSortedReports.length / perPage),
  );
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedReports = filteredAndSortedReports.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage,
  );

  // handle export to excel
  const handleExportToExcel = () => {
    if (filteredAndSortedReports.length === 0) {
      toast.error(
        t("reports.workingHours.noDataToExport", {
          defaultValue: "No data to export",
        }),
        { rtl: isRTL },
      );
      return;
    }

    const data = filteredAndSortedReports.map((item, index) => ({
      [t("reports.workingHours.excel.serial", {
        defaultValue: isRTL ? "م" : "Serial",
      })]: index + 1,
      [t("reports.workingHours.excel.employee", {
        defaultValue: isRTL ? "الموظف" : "Employee",
      })]:
        item.employee_name ||
        item.employee?.name ||
        t("reports.workingHours.notAvailable", {
          defaultValue: "Not Available",
        }),
      [t("employeeLoansNamespace.department", {
        defaultValue: isRTL ? "القسم" : "Department",
      })]: item.department || "-",
      [t("employeeLoansNamespace.amount", {
        defaultValue: isRTL ? "المبلغ" : "Amount",
      })]: item.amount || 0,
      [t("employeeLoansNamespace.monthlyDeduction", {
        defaultValue: isRTL ? "الخصم الشهري" : "Monthly Deduction",
      })]: item.monthly_deduction || 0,
      [t("employeeLoansNamespace.installmentsCount", {
        defaultValue: isRTL ? "عدد الأقساط" : "Installments",
      })]: item.installments_count || 0,
      [t("employeeLoansNamespace.paidAmount", {
        defaultValue: isRTL ? "المدفوع" : "Paid",
      })]: getPaidAmount(item),
      [t("employeeLoansNamespace.remainingBalance", {
        defaultValue: isRTL ? "المتبقي" : "Remaining",
      })]: getRemainingAmount(item),
      [t("employeeLoansNamespace.status", {
        defaultValue: isRTL ? "الحالة" : "Status",
      })]: t(`employeeLoansNamespace.${item.status}`, {
        defaultValue: item.status,
      }),
      [t("employeeLoansNamespace.loanDate", {
        defaultValue: isRTL ? "تاريخ السلفة" : "Loan Date",
      })]: item.loan_date || item.date || item.created_at?.split("T")[0] || "-",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      wb,
      ws,
      t("employeeLoansNamespace.print.fileName", {
        defaultValue: "Loans_Report",
      }),
    );
    const fileName = reportDate
      ? `${t("employeeLoansNamespace.print.fileName", { defaultValue: "Loans_Report" })}_${reportDate}.xlsx`
      : `${t("employeeLoansNamespace.print.fileName", { defaultValue: "Loans_Report" })}_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(
      t("reports.workingHours.exportSuccess", {
        defaultValue: "Export success",
      }),
      { rtl: isRTL },
    );
  };

  // handle print to pdf
  const handlePrintPdf = async () => {
    if (!filteredAndSortedReports || filteredAndSortedReports.length === 0) {
      toast.error(
        t("reports.workingHours.print.noData", {
          defaultValue: "No data to print",
        }),
        { rtl: isRTL },
      );
      return;
    }
    try {
      setIsGeneratingPdf(true);
      const pdf = new jsPDF("p", "mm", "a4");

      for (let i = 0; i < pdfChunks.length; i++) {
        const element = document.getElementById(`loans-report-pdf-chunk-${i}`);
        if (!element) continue;

        const canvas = await html2canvas(element, {
          scale: 3,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `loans-report-pdf-chunk-${i}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = isRTL ? "rtl" : "ltr";
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, 210, 297);
      }

      pdf.save(`ACE_Loans_Report_${reportDate}.pdf`);
      toast.success(
        t("reports.workingHours.print.success", {
          defaultValue: "PDF Generated Successfully",
        }),
      );
    } catch (error) {
      console.error("PDF generation error:", error);
      toast.error(
        t("reports.workingHours.print.error", {
          defaultValue: "Error generating PDF",
        }),
      );
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
      }}
    >
      {/* Title */}
      <div style={{ marginBottom: "30px" }}>
        <h1
          style={{
            fontSize: "24px",
            fontWeight: "bold",
            color: "white",
            marginBottom: "20px",
            textAlign,
          }}
        >
          {t("employeeLoansNamespace.title", {
            defaultValue: "Employee Loans",
          })}
        </h1>
      </div>

      {/* Date Select and General Actions */}
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "20px",
          border: "1px solid #333",
          marginBottom: "30px",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "15px",
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: "1", minWidth: "200px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                color: "white",
                fontSize: "14px",
                fontWeight: "500",
                textAlign,
                direction: dir,
              }}
            >
              {t("reports.workingHours.monthYear", {
                defaultValue: "Month/Year",
              })}
            </label>
            <input
              type="month"
              value={reportDate}
              onChange={e => {
                setReportDate(e.target.value);
                setAllReports([]);
                setHasGenerated(false);
                setIsFetchingReport(false);
                dispatch(clearReports());
              }}
              className="month-input-white"
              style={{
                width: "100%",
                padding: "12px 15px",
                backgroundColor: "#1a1f2e",
                border: "1px solid #333",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                direction: dir,
                textAlign,
              }}
            />
          </div>
          <button
            onClick={handleGenerateReport}
            disabled={isLoading || isFetchingReport}
            style={{
              backgroundColor:
                isLoading || isFetchingReport ? "#666" : "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              cursor: isLoading || isFetchingReport ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "bold",
              opacity: isLoading || isFetchingReport ? 0.6 : 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FaCalendarAlt />
            {isLoading || isFetchingReport
              ? t("reports.workingHours.generating", {
                  defaultValue: "Generating...",
                })
              : t("reports.workingHours.showReport", {
                  defaultValue: "Show Report",
                })}
          </button>
          {hasGenerated &&
            !isFetchingReport &&
            filteredAndSortedReports &&
            filteredAndSortedReports.length > 0 && (
              <>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <label
                    style={{
                      color: "white",
                      fontSize: "14px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("common.perPage", { defaultValue: "عدد السجلات" })}:
                  </label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <button
                      onClick={() =>
                        setPerPageInput(Math.max(1, perPageInput - 1))
                      }
                      style={{
                        backgroundColor: "#1b3350",
                        color: "white",
                        border: "1px solid #333",
                        padding: "6px 10px",
                        borderRadius: "6px 0 0 6px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "32px",
                      }}
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      max={filteredAndSortedReports?.length || 1000}
                      value={perPageInput}
                      onChange={e =>
                        setPerPageInput(
                          Math.max(1, parseInt(e.target.value) || 1),
                        )
                      }
                      style={{
                        width: "70px",
                        padding: "6px 10px",
                        backgroundColor: "#1a1f2e",
                        border: "1px solid #333",
                        borderLeft: "none",
                        borderRight: "none",
                        borderRadius: 0,
                        color: "white",
                        fontSize: "14px",
                        textAlign: "center",
                      }}
                    />
                    <button
                      onClick={() =>
                        setPerPageInput(
                          Math.min(
                            filteredAndSortedReports?.length || 1000,
                            perPageInput + 1,
                          ),
                        )
                      }
                      style={{
                        backgroundColor: "#1b3350",
                        color: "white",
                        border: "1px solid #333",
                        padding: "6px 10px",
                        borderRadius: "0 6px 6px 0",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "bold",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: "32px",
                      }}
                    >
                      +
                    </button>
                  </div>
                  <button
                    onClick={handleApplyPerPage}
                    style={{
                      backgroundColor: "#AC2000",
                      color: "white",
                      border: "none",
                      padding: "6px 12px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "13px",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {t("common.apply", { defaultValue: "تطبيق" })}
                  </button>
                </div>
                <button
                  onClick={() => setIsPreviewOpen(true)}
                  style={{
                    backgroundColor: "#444",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexDirection: isRTL ? "row" : "row-reverse",
                  }}
                >
                  <FaEye />
                  {t("reports.workingHours.preview", {
                    defaultValue: "Preview",
                  })}
                </button>
                <button
                  onClick={handlePrintPdf}
                  disabled={isGeneratingPdf}
                  style={{
                    backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    opacity: isGeneratingPdf ? 0.6 : 1,
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexDirection: isRTL ? "row" : "row-reverse",
                  }}
                >
                  <FaPrint />
                  {isGeneratingPdf
                    ? t("reports.workingHours.print.generating", {
                        defaultValue: "Generating PDF...",
                      })
                    : t("common.print", {
                        defaultValue: isRTL ? "طباعة PDF" : "Print PDF",
                      })}
                </button>
                <button
                  onClick={handleExportToExcel}
                  style={{
                    backgroundColor: "#3b82f6",
                    color: "white",
                    border: "none",
                    padding: "12px 24px",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "14px",
                    fontWeight: "bold",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    flexDirection: isRTL ? "row" : "row-reverse",
                  }}
                >
                  <FaFileExcel />
                  {t("reports.workingHours.exportExcel", {
                    defaultValue: "Export Excel",
                  })}
                </button>
              </>
            )}
        </div>
      </div>

      {/* Results View */}
      {hasGenerated &&
        !isFetchingReport &&
        allReports &&
        allReports.length > 0 && (
          <div
            style={{
              backgroundColor: "#1b3350",
              borderRadius: "12px",
              padding: "20px",
              border: "1px solid #333",
            }}
          >
            {/* Search, Filters, and Sorting Controls */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "15px",
                marginBottom: "20px",
                alignItems: "flex-end",
                borderBottom: "1px solid #333",
                paddingBottom: "20px",
              }}
            >
              {/* Search Input */}
              <div
                style={{
                  flex: "1",
                  minWidth: "200px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <label
                  style={{ color: "white", fontSize: "13px", direction: dir }}
                >
                  {t("common.search", {
                    defaultValue: isRTL ? "بحث" : "Search",
                  })}
                </label>
                <input
                  type="text"
                  placeholder={t("reports.payroll.searchPlaceholder", {
                    defaultValue: "Search employee...",
                  })}
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "12px 15px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    direction: dir,
                    textAlign,
                  }}
                />
              </div>

              {/* Department Filter */}
              <div
                style={{
                  minWidth: "180px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <label
                  style={{ color: "white", fontSize: "13px", direction: dir }}
                >
                  {t("employeeLoansNamespace.department", {
                    defaultValue: "Department",
                  })}
                </label>
                <select
                  value={deptFilter}
                  onChange={e => {
                    setDeptFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "12px 15px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    direction: dir,
                  }}
                >
                  <option value="all">
                    {t("common.all", { defaultValue: "All" })}
                  </option>
                  {uniqueDepartments.map(dept => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </select>
              </div>

              {/* Status Filter */}
              <div
                style={{
                  minWidth: "180px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <label
                  style={{ color: "white", fontSize: "13px", direction: dir }}
                >
                  {t("employeeLoansNamespace.status", {
                    defaultValue: "Status",
                  })}
                </label>
                <select
                  value={statusFilter}
                  onChange={e => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    padding: "12px 15px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    direction: dir,
                  }}
                >
                  <option value="all">
                    {t("common.all", { defaultValue: "All" })}
                  </option>
                  <option value="active">
                    {t("employeeLoansNamespace.active", {
                      defaultValue: "Active",
                    })}
                  </option>
                  <option value="paid">
                    {t("employeeLoansNamespace.paid", { defaultValue: "Paid" })}
                  </option>
                  <option value="approved">
                    {t("employeeLoansNamespace.approved", {
                      defaultValue: "Approved",
                    })}
                  </option>
                  <option value="pending">
                    {t("employeeLoansNamespace.pending", {
                      defaultValue: "Pending",
                    })}
                  </option>
                </select>
              </div>

              {/* Sorting Field */}
              <div
                style={{
                  minWidth: "180px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                <label
                  style={{ color: "white", fontSize: "13px", direction: dir }}
                >
                  {t("common.sortBy", { defaultValue: "Sort by" })}
                </label>
                <select
                  value={sortField}
                  onChange={e => setSortField(e.target.value)}
                  style={{
                    padding: "12px 15px",
                    backgroundColor: "#1a1f2e",
                    border: "1px solid #333",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "14px",
                    direction: dir,
                  }}
                >
                  <option value="employee_name">
                    {t("employeeLoansNamespace.employee", {
                      defaultValue: "Employee",
                    })}
                  </option>
                  <option value="department">
                    {t("employeeLoansNamespace.department", {
                      defaultValue: "Department",
                    })}
                  </option>
                  <option value="amount">
                    {t("employeeLoansNamespace.amount", {
                      defaultValue: "Amount",
                    })}
                  </option>
                  <option value="paid">
                    {t("employeeLoansNamespace.paidAmount", {
                      defaultValue: "Paid",
                    })}
                  </option>
                  <option value="remaining">
                    {t("employeeLoansNamespace.remainingBalance", {
                      defaultValue: "Remaining",
                    })}
                  </option>
                  <option value="status">
                    {t("employeeLoansNamespace.status", {
                      defaultValue: "Status",
                    })}
                  </option>
                  <option value="loan_date">
                    {t("employeeLoansNamespace.loanDate", {
                      defaultValue: "Date",
                    })}
                  </option>
                </select>
              </div>

              {/* Sort Order */}
              <SortOrderSelect
                value={sortOrder}
                onChange={setSortOrder}
                style={{ minWidth: "150px" }}
                selectStyle={{ padding: "12px 15px", height: "45px" }}
              />

              {/* Reset Button */}
              <button
                onClick={() => {
                  setSearchTerm("");
                  setDeptFilter("all");
                  setStatusFilter("all");
                  setSortField("employee_name");
                  setSortOrder("asc");
                  setCurrentPage(1);
                }}
                style={{
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "12px 20px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  height: "45px",
                  whiteSpace: "nowrap",
                }}
              >
                {t("common.resetFilters", { defaultValue: "Clear Filters" })}
              </button>
            </div>

            <div style={{ overflow: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  direction: dir,
                }}
              >
                <thead>
                  <tr style={{ borderBottom: "2px solid #AC2000" }}>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: "60px",
                      }}
                    >
                      {t("reports.workingHours.table.number", {
                        defaultValue: "#",
                      })}
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: "150px",
                      }}
                    >
                      {t("employeeLoansNamespace.employee", {
                        defaultValue: "Employee",
                      })}
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: "160px",
                      }}
                    >
                      {t("employeeLoansNamespace.department", {
                        defaultValue: "Department",
                      })}
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: "130px",
                      }}
                    >
                      {t("employeeLoansNamespace.amount", {
                        defaultValue: "Amount",
                      })}
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: "140px",
                      }}
                    >
                      {t("employeeLoansNamespace.monthlyDeduction", {
                        defaultValue: "Monthly Deduction",
                      })}
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: "130px",
                      }}
                    >
                      {t("employeeLoansNamespace.installmentsCount", {
                        defaultValue: "Installments",
                      })}
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: "130px",
                      }}
                    >
                      {t("employeeLoansNamespace.paidAmount", {
                        defaultValue: "Paid",
                      })}
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: "130px",
                      }}
                    >
                      {t("employeeLoansNamespace.remainingBalance", {
                        defaultValue: "Remaining",
                      })}
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: "130px",
                      }}
                    >
                      {t("employeeLoansNamespace.status", {
                        defaultValue: "Status",
                      })}
                    </th>
                    <th
                      style={{
                        padding: "16px 20px",
                        textAlign: "center",
                        verticalAlign: "middle",
                        color: "white",
                        fontWeight: "bold",
                        minWidth: "165px",
                      }}
                    >
                      {t("employeeLoansNamespace.loanDate", {
                        defaultValue: "Date",
                      })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedReports.map((item, index) => (
                    <tr
                      key={item.id || index}
                      style={{ borderBottom: "1px solid #333" }}
                    >
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          color: "white",
                        }}
                      >
                        {filteredAndSortedReports.indexOf(item) + 1}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          color: "white",
                          direction: dir,
                        }}
                      >
                        {item.employee_name ||
                          (item.employee
                            ? (isRTL
                                ? item.employee.name_ar
                                : item.employee.name_en) || item.employee.name
                            : t("reports.workingHours.notAvailable", {
                                defaultValue: "N/A",
                              }))}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          color: "white",
                          direction: dir,
                        }}
                      >
                        {item.department || "-"}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          color: "white",
                          direction: dir,
                        }}
                      >
                        {item.amount || 0}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          color: "white",
                          direction: dir,
                        }}
                      >
                        {item.monthly_deduction || 0}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          color: "white",
                          direction: dir,
                        }}
                      >
                        {item.installments_count || 0}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          color: "white",
                          direction: dir,
                        }}
                      >
                        {getPaidAmount(item)}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          color: "white",
                          direction: dir,
                        }}
                      >
                        {getRemainingAmount(item)}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          color: "white",
                          direction: dir,
                        }}
                      >
                        {t(`employeeLoansNamespace.${item.status}`, {
                          defaultValue: item.status,
                        })}
                      </td>
                      <td
                        style={{
                          padding: "16px 20px",
                          textAlign: "center",
                          verticalAlign: "middle",
                          color: "white",
                          fontSize: "14px",
                          direction: dir,
                        }}
                      >
                        {item.loan_date ||
                          item.date ||
                          item.created_at?.split("T")[0] ||
                          "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  gap: "8px",
                  marginTop: "20px",
                  paddingBottom: "20px",
                  direction: dir,
                }}
              >
                <button
                  onClick={() =>
                    setCurrentPage(Math.max(1, currentPageSafe - 1))
                  }
                  disabled={currentPageSafe === 1}
                  style={{
                    padding: "8px 12px",
                    backgroundColor: currentPageSafe === 1 ? "#333" : "#AC2000",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: currentPageSafe === 1 ? "not-allowed" : "pointer",
                  }}
                >
                  {t("common.previous", { defaultValue: "Previous" })}
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(
                    page =>
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPageSafe - 2 &&
                        page <= currentPageSafe + 2),
                  )
                  .map((page, idx, arr) => {
                    if (idx > 0 && page - arr[idx - 1] > 1) {
                      return (
                        <React.Fragment key={`ellipsis-${page}`}>
                          <span style={{ padding: "8px", color: "white" }}>
                            ...
                          </span>
                          <button
                            onClick={() => setCurrentPage(page)}
                            style={{
                              padding: "8px 12px",
                              backgroundColor:
                                page === currentPageSafe
                                  ? "#AC2000"
                                  : "#1a1f2e",
                              color: "white",
                              border: "1px solid #333",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    }
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        style={{
                          padding: "8px 12px",
                          backgroundColor:
                            page === currentPageSafe ? "#AC2000" : "#1a1f2e",
                          color: "white",
                          border: "1px solid #333",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      >
                        {page}
                      </button>
                    );
                  })}
                <button
                  onClick={() =>
                    setCurrentPage(Math.min(totalPages, currentPageSafe + 1))
                  }
                  disabled={currentPageSafe === totalPages}
                  style={{
                    padding: "8px 12px",
                    backgroundColor:
                      currentPageSafe === totalPages ? "#333" : "#AC2000",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor:
                      currentPageSafe === totalPages
                        ? "not-allowed"
                        : "pointer",
                  }}
                >
                  {t("common.next", { defaultValue: "Next" })}
                </button>
              </div>
            )}
          </div>
        )}

      {/* No Data Message */}
      {hasGenerated &&
        !isFetchingReport &&
        loansReports &&
        (!allReports || allReports.length === 0) && (
          <div
            style={{
              backgroundColor: "#1b3350",
              borderRadius: "12px",
              padding: "40px 20px",
              border: "1px solid #333",
              textAlign: "center",
            }}
          >
            <div
              style={{
                color: "#999",
                fontSize: "18px",
                fontWeight: "500",
              }}
            >
              {t("reports.workingHours.noData", {
                defaultValue: "No data found",
              })}
            </div>
          </div>
        )}

      {/* PDF Measurement Element - Hidden offscreen */}
      {hasGenerated &&
        !isFetchingReport &&
        filteredAndSortedReports &&
        filteredAndSortedReports.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "-99999px",
              left: "-99999px",
              width: "794px",
            }}
          >
            <div
              style={{
                width: "794px",
                backgroundColor: "#ffffff",
                color: "#000000",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                padding: "40px",
                boxSizing: "border-box",
                direction: dir,
              }}
            >
              <div
                style={{
                  border: "5px solid #000",
                  padding: "3px",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                }}
              >
                <div
                  style={{
                    border: "2px solid #000",
                    padding: "18px 18px 10px",
                    display: "flex",
                    flexDirection: "column",
                    direction: dir,
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                  }}
                >
                  <div
                    id="pdf-measure-top-header"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        marginBottom: "8px",
                        borderBottom: "4px double #000",
                        paddingBottom: "8px",
                      }}
                      crossOrigin="anonymous"
                    />
                    <div style={{ textAlign: "center", marginBottom: "16px" }}>
                      <div
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#0d2a50",
                        }}
                      >
                        {t("employeeLoansNamespace.title", {
                          defaultValue: "تقرير السلف",
                        })}
                      </div>
                      <div style={{ fontSize: "14px", marginTop: "4px" }}>
                        {reportDate
                          ? new Date(reportDate + "-01").toLocaleDateString(
                              locale,
                              { year: "numeric", month: "long" },
                            )
                          : ""}
                      </div>
                      {/* Applied Filters info */}
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#333",
                          marginTop: "4px",
                          display: "flex",
                          justifyContent: "center",
                          gap: "15px",
                        }}
                      >
                        {searchTerm.trim() && (
                          <span>
                            {t("common.search", { defaultValue: "بحث" })}:{" "}
                            {searchTerm}
                          </span>
                        )}
                        {deptFilter !== "all" && (
                          <span>
                            {t("employeeLoansNamespace.department")}:{" "}
                            {deptFilter}
                          </span>
                        )}
                        {statusFilter !== "all" && (
                          <span>
                            {t("employeeLoansNamespace.status")}: {statusFilter}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#555",
                          marginTop: "2px",
                        }}
                      >
                        {t("reports.workingHours.print.employeesCount", {
                          defaultValue: "Employees Count",
                        })}
                        : {filteredAndSortedReports.length}
                      </div>
                    </div>
                  </div>

                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: "9px",
                      tableLayout: "fixed",
                    }}
                  >
                    <colgroup>
                      <col style={{ width: "5%" }} />
                      <col style={{ width: "18%" }} />
                      <col style={{ width: "13%" }} />
                      <col style={{ width: "11%" }} />
                      <col style={{ width: "11%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "11%" }} />
                      <col style={{ width: "11%" }} />
                      <col style={{ width: "11%" }} />
                      <col style={{ width: "11%" }} />
                    </colgroup>
                    <thead id="pdf-measure-thead">
                      <tr
                        style={{ backgroundColor: "#e3f2fd", color: "#0d2a50" }}
                      >
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("reports.workingHours.print.serial", {
                            defaultValue: "م",
                          })}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: isRTL ? "right" : "left",
                          }}
                        >
                          {t("employeeLoansNamespace.employee")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeeLoansNamespace.department")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeeLoansNamespace.amount")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeeLoansNamespace.monthlyDeduction")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeeLoansNamespace.installmentsCount")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeeLoansNamespace.paidAmount")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeeLoansNamespace.remainingBalance")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeeLoansNamespace.status")}
                        </th>
                        <th
                          style={{
                            border: "1px solid #000",
                            padding: "4px 2px",
                            textAlign: "center",
                          }}
                        >
                          {t("employeeLoansNamespace.loanDate")}
                        </th>
                      </tr>
                    </thead>
                    <tbody id="pdf-measure-tbody">
                      {filteredAndSortedReports.map((item, index) => {
                        const cell = {
                          border: "1px solid #000",
                          padding: "4px 2px",
                          fontSize: "8px",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        };
                        return (
                          <tr key={item.id || index}>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {index + 1}
                            </td>
                            <td
                              style={{
                                ...cell,
                                textAlign: isRTL ? "right" : "left",
                              }}
                            >
                              {item.employee_name ||
                                (item.employee
                                  ? (isRTL
                                      ? item.employee.name_ar
                                      : item.employee.name_en) ||
                                    item.employee.name
                                  : "—")}
                            </td>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {item.department || "-"}
                            </td>
                            <td
                              style={{
                                ...cell,
                                textAlign: "center",
                                fontWeight: "700",
                              }}
                            >
                              {item.amount || 0}
                            </td>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {item.monthly_deduction || 0}
                            </td>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {item.installments_count || 0}
                            </td>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {getPaidAmount(item)}
                            </td>
                            <td
                              style={{
                                ...cell,
                                textAlign: "center",
                                fontWeight: "700",
                                color: "#AC2000",
                              }}
                            >
                              {getRemainingAmount(item)}
                            </td>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {t(`employeeLoansNamespace.${item.status}`, {
                                defaultValue: item.status,
                              })}
                            </td>
                            <td style={{ ...cell, textAlign: "center" }}>
                              {item.loan_date ||
                                item.date ||
                                item.created_at?.split("T")[0] ||
                                "-"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  <div
                    id="pdf-measure-footer"
                    style={{ marginTop: "auto", paddingTop: "6px" }}
                  >
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      {/* PDF Template - Hidden (For actual printing) */}
      {pdfChunks && pdfChunks.length > 0 && (
        <div
          style={{ position: "absolute", top: "-99999px", left: "-99999px" }}
        >
          {pdfChunks.map((chunk, chunkIdx, allChunks) => (
            <div
              key={chunkIdx}
              id={`loans-report-pdf-chunk-${chunkIdx}`}
              style={{
                width: "794px",
                height: "1123px",
                backgroundColor: "#ffffff",
                color: "#000000",
                fontFamily:
                  '"Times New Roman Bold", "Times New Roman", "Times", serif',
                fontWeight: "bold",
                padding: "40px",
                boxSizing: "border-box",
                direction: dir,
              }}
            >
              <div
                style={{
                  border: "5px solid #000",
                  padding: "3px",
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    border: "2px solid #000",
                    height: "100%",
                    padding: "18px 18px 10px",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    fontFamily:
                      '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    fontWeight: "bold",
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                    textRendering: "optimizeLegibility",
                    direction: dir,
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "100%",
                      height: "600px",
                      backgroundImage: `url(${headerLogo})`,
                      backgroundSize: "95%",
                      backgroundPosition: "center center",
                      backgroundRepeat: "no-repeat",
                      opacity: 0.1,
                      zIndex: 0,
                      pointerEvents: "none",
                    }}
                  />
                  <div
                    style={{
                      position: "relative",
                      zIndex: 1,
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                    }}
                  >
                    <img
                      src={`${publicUrl}/HeaderAce.png`}
                      alt="Header"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                        marginBottom: "8px",
                        borderBottom: "4px double #000",
                        paddingBottom: "8px",
                        flexShrink: 0,
                      }}
                      crossOrigin="anonymous"
                    />
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        minHeight: 0,
                      }}
                    >
                      <div
                        style={{
                          textAlign: "center",
                          marginBottom: "16px",
                          flexShrink: 0,
                        }}
                      >
                        <div
                          style={{
                            fontSize: "20px",
                            fontWeight: "700",
                            color: "#0d2a50",
                            direction: dir,
                          }}
                        >
                          {t("employeeLoansNamespace.title", {
                            defaultValue: "تقرير السلف",
                          })}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            marginTop: "4px",
                            direction: dir,
                          }}
                        >
                          {reportDate
                            ? new Date(reportDate + "-01").toLocaleDateString(
                                locale,
                                { year: "numeric", month: "long" },
                              )
                            : ""}
                        </div>
                        {/* Applied Filters info */}
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#333",
                            marginTop: "4px",
                            display: "flex",
                            justifyContent: "center",
                            gap: "15px",
                            direction: dir,
                          }}
                        >
                          {searchTerm.trim() && (
                            <span>
                              {t("common.search", { defaultValue: "بحث" })}:{" "}
                              {searchTerm}
                            </span>
                          )}
                          {deptFilter !== "all" && (
                            <span>
                              {t("employeeLoansNamespace.department")}:{" "}
                              {deptFilter}
                            </span>
                          )}
                          {statusFilter !== "all" && (
                            <span>
                              {t("employeeLoansNamespace.status")}:{" "}
                              {statusFilter}
                            </span>
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: "13px",
                            color: "#555",
                            marginTop: "2px",
                            direction: dir,
                          }}
                        >
                          {t("reports.workingHours.print.employeesCount", {
                            defaultValue: "Employees Count",
                          })}
                          : {filteredAndSortedReports.length}
                        </div>
                      </div>
                      <div style={{ flexShrink: 0 }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "9px",
                            direction: dir,
                            tableLayout: "fixed",
                          }}
                        >
                          <colgroup>
                            <col style={{ width: "5%" }} />
                            <col style={{ width: "18%" }} />
                            <col style={{ width: "13%" }} />
                            <col style={{ width: "11%" }} />
                            <col style={{ width: "11%" }} />
                            <col style={{ width: "8%" }} />
                            <col style={{ width: "11%" }} />
                            <col style={{ width: "11%" }} />
                            <col style={{ width: "11%" }} />
                            <col style={{ width: "11%" }} />
                          </colgroup>
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "#e3f2fd",
                                color: "#0d2a50",
                              }}
                            >
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 2px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("reports.workingHours.print.serial", {
                                  defaultValue: "م",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 2px",
                                  direction: dir,
                                  textAlign: isRTL ? "right" : "left",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("employeeLoansNamespace.employee")}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 2px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("employeeLoansNamespace.department")}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 2px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("employeeLoansNamespace.amount")}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 2px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("employeeLoansNamespace.monthlyDeduction")}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 2px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("employeeLoansNamespace.installmentsCount")}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 2px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("employeeLoansNamespace.paidAmount")}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 2px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("employeeLoansNamespace.remainingBalance")}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 2px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("employeeLoansNamespace.status")}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "4px 2px",
                                  direction: dir,
                                  textAlign: "center",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {t("employeeLoansNamespace.loanDate")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {chunk.map((item, index) => {
                              const cell = {
                                border: "1px solid #000",
                                padding: "4px 2px",
                                fontSize: "8px",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                textOverflow: "ellipsis",
                              };
                              return (
                                <tr key={item.id || index}>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      direction: dir,
                                    }}
                                  >
                                    {filteredAndSortedReports.indexOf(item) + 1}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      direction: dir,
                                      textAlign: isRTL ? "right" : "left",
                                    }}
                                  >
                                    {item.employee_name ||
                                      (item.employee
                                        ? (isRTL
                                            ? item.employee.name_ar
                                            : item.employee.name_en) ||
                                          item.employee.name
                                        : "—")}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      direction: dir,
                                      textAlign: "center",
                                    }}
                                  >
                                    {item.department || "-"}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      direction: dir,
                                    }}
                                  >
                                    {item.amount || 0}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      direction: dir,
                                    }}
                                  >
                                    {item.monthly_deduction || 0}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      direction: dir,
                                    }}
                                  >
                                    {item.installments_count || 0}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      direction: dir,
                                    }}
                                  >
                                    {getPaidAmount(item)}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      fontWeight: "700",
                                      color: "#AC2000",
                                      direction: dir,
                                    }}
                                  >
                                    {getRemainingAmount(item)}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      direction: dir,
                                    }}
                                  >
                                    {t(
                                      `employeeLoansNamespace.${item.status}`,
                                      { defaultValue: item.status },
                                    )}
                                  </td>
                                  <td
                                    style={{
                                      ...cell,
                                      textAlign: "center",
                                      direction: dir,
                                    }}
                                  >
                                    {item.loan_date ||
                                      item.date ||
                                      item.created_at?.split("T")[0] ||
                                      "-"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <div
                        style={{
                          flex: 1,
                          borderLeft: "1px solid #000",
                          borderRight: "1px solid #000",
                          borderBottom: "1px solid #000",
                          backgroundColor: "rgba(255,255,255,0.7)",
                        }}
                      />
                    </div>
                    <div style={{ flexShrink: 0, paddingTop: "6px" }}>
                      <img
                        src={`${publicUrl}/FooterAce.png`}
                        alt="Footer"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                        }}
                        crossOrigin="anonymous"
                      />
                      <div
                        style={{
                          textAlign: "center",
                          marginTop: "4px",
                          fontFamily: '"Times New Roman", "Times", serif',
                        }}
                      >
                        <div
                          style={{
                            fontStyle: "italic",
                            display: "inline-flex",
                            alignItems: "baseline",
                            gap: "6px",
                            fontSize: "12px",
                            fontWeight: "bold",
                            direction: "ltr",
                          }}
                        >
                          <span>Page</span>
                          <strong style={{ fontSize: "13px" }}>
                            {chunkIdx + 1} / {allChunks.length}
                          </strong>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* PDF Preview Modal */}
      <PdfPreviewModal
        isOpen={
          isPreviewOpen &&
          filteredAndSortedReports &&
          filteredAndSortedReports.length > 0
        }
        title={t("employeeLoansNamespace.title", { defaultValue: "Loans" })}
        onClose={() => setIsPreviewOpen(false)}
        onPrint={handlePrintPdf}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div
          id="preview-loans-report-pdf"
          style={{
            width: "794px",
            backgroundColor: "#ffffff",
            color: "#000000",
            fontFamily: '"Times New Roman", "Times", serif',
            boxSizing: "border-box",
            direction: dir,
            transform: "scale(1.0)",
            transformOrigin: "top center",
          }}
        >
          {pdfChunks &&
            pdfChunks.map((chunk, chunkIdx, allChunks) => (
              <div
                key={chunkIdx}
                style={{
                  width: "794px",
                  height: "1123px",
                  padding: "40px",
                  boxSizing: "border-box",
                  backgroundColor: "#ffffff",
                  marginBottom: chunkIdx < allChunks.length - 1 ? "20px" : "0",
                  margin: "0 auto",
                }}
              >
                <div
                  style={{
                    border: "5px solid #000",
                    padding: "3px",
                    backgroundColor: "#ffffff",
                    boxSizing: "border-box",
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      border: "2px solid #000",
                      height: "100%",
                      padding: "18px 18px 10px",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                      fontWeight: "bold",
                      WebkitFontSmoothing: "antialiased",
                      MozOsxFontSmoothing: "grayscale",
                      textRendering: "optimizeLegibility",
                      direction: dir,
                      backgroundColor: "#ffffff",
                      boxSizing: "border-box",
                      width: "100%",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: "100%",
                        height: "600px",
                        backgroundImage: `url(${headerLogo})`,
                        backgroundSize: "95%",
                        backgroundPosition: "center center",
                        backgroundRepeat: "no-repeat",
                        opacity: 0.1,
                        zIndex: 0,
                        pointerEvents: "none",
                      }}
                    />
                    <div
                      style={{
                        position: "relative",
                        zIndex: 1,
                        display: "flex",
                        flexDirection: "column",
                        height: "100%",
                      }}
                    >
                      <img
                        src={`${publicUrl}/HeaderAce.png`}
                        alt="Header"
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "contain",
                          marginBottom: "8px",
                          borderBottom: "4px double #000",
                          paddingBottom: "8px",
                          flexShrink: 0,
                        }}
                        crossOrigin="anonymous"
                      />
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                          minHeight: 0,
                        }}
                      >
                        <div
                          style={{
                            textAlign: "center",
                            marginBottom: "16px",
                            flexShrink: 0,
                          }}
                        >
                          <div
                            style={{
                              fontSize: "20px",
                              fontWeight: "700",
                              color: "#0d2a50",
                              direction: dir,
                            }}
                          >
                            {t("employeeLoansNamespace.title", {
                              defaultValue: "تقرير السلف",
                            })}
                          </div>
                          <div
                            style={{
                              fontSize: "14px",
                              marginTop: "4px",
                              direction: dir,
                            }}
                          >
                            {reportDate
                              ? new Date(reportDate + "-01").toLocaleDateString(
                                  locale,
                                  { year: "numeric", month: "long" },
                                )
                              : ""}
                          </div>
                          {/* Applied Filters info */}
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#333",
                              marginTop: "4px",
                              display: "flex",
                              justifyContent: "center",
                              gap: "15px",
                              direction: dir,
                            }}
                          >
                            {searchTerm.trim() && (
                              <span>
                                {t("common.search", { defaultValue: "بحث" })}:{" "}
                                {searchTerm}
                              </span>
                            )}
                            {deptFilter !== "all" && (
                              <span>
                                {t("employeeLoansNamespace.department")}:{" "}
                                {deptFilter}
                              </span>
                            )}
                            {statusFilter !== "all" && (
                              <span>
                                {t("employeeLoansNamespace.status")}:{" "}
                                {statusFilter}
                              </span>
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#555",
                              marginTop: "2px",
                              direction: dir,
                            }}
                          >
                            {t("reports.workingHours.print.employeesCount", {
                              defaultValue: "Employees Count",
                            })}
                            : {filteredAndSortedReports.length}
                          </div>
                        </div>
                        <div style={{ flexShrink: 0 }}>
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                              fontSize: "9px",
                              direction: dir,
                              tableLayout: "fixed",
                            }}
                          >
                            <colgroup>
                              <col style={{ width: "5%" }} />
                              <col style={{ width: "18%" }} />
                              <col style={{ width: "13%" }} />
                              <col style={{ width: "11%" }} />
                              <col style={{ width: "11%" }} />
                              <col style={{ width: "8%" }} />
                              <col style={{ width: "11%" }} />
                              <col style={{ width: "11%" }} />
                              <col style={{ width: "11%" }} />
                              <col style={{ width: "11%" }} />
                            </colgroup>
                            <thead>
                              <tr
                                style={{
                                  backgroundColor: "#e3f2fd",
                                  color: "#0d2a50",
                                }}
                              >
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 2px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("reports.workingHours.print.serial", {
                                    defaultValue: "م",
                                  })}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 2px",
                                    direction: dir,
                                    textAlign: isRTL ? "right" : "left",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("employeeLoansNamespace.employee")}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 2px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("employeeLoansNamespace.department")}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 2px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("employeeLoansNamespace.amount")}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 2px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("employeeLoansNamespace.monthlyDeduction")}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 2px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t(
                                    "employeeLoansNamespace.installmentsCount",
                                  )}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 2px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("employeeLoansNamespace.paidAmount")}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 2px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("employeeLoansNamespace.remainingBalance")}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 2px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("employeeLoansNamespace.status")}
                                </th>
                                <th
                                  style={{
                                    border: "1px solid #000",
                                    padding: "4px 2px",
                                    direction: dir,
                                    textAlign: "center",
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {t("employeeLoansNamespace.loanDate")}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {chunk.map((item, index) => {
                                const cell = {
                                  border: "1px solid #000",
                                  padding: "4px 2px",
                                  fontSize: "8px",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  textOverflow: "ellipsis",
                                };
                                return (
                                  <tr key={item.id || index}>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        direction: dir,
                                      }}
                                    >
                                      {filteredAndSortedReports.indexOf(item) +
                                        1}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        direction: dir,
                                        textAlign: isRTL ? "right" : "left",
                                      }}
                                    >
                                      {item.employee_name ||
                                        (item.employee
                                          ? (isRTL
                                              ? item.employee.name_ar
                                              : item.employee.name_en) ||
                                            item.employee.name
                                          : "—")}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        direction: dir,
                                        textAlign: "center",
                                      }}
                                    >
                                      {item.department || "-"}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        direction: dir,
                                      }}
                                    >
                                      {item.amount || 0}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        direction: dir,
                                      }}
                                    >
                                      {item.monthly_deduction || 0}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        direction: dir,
                                      }}
                                    >
                                      {item.installments_count || 0}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        direction: dir,
                                      }}
                                    >
                                      {getPaidAmount(item)}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        fontWeight: "700",
                                        color: "#AC2000",
                                        direction: dir,
                                      }}
                                    >
                                      {getRemainingAmount(item)}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        direction: dir,
                                      }}
                                    >
                                      {t(
                                        `employeeLoansNamespace.${item.status}`,
                                        { defaultValue: item.status },
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        ...cell,
                                        textAlign: "center",
                                        direction: dir,
                                      }}
                                    >
                                      {item.loan_date ||
                                        item.date ||
                                        item.created_at?.split("T")[0] ||
                                        "-"}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div
                          style={{
                            flex: 1,
                            borderLeft: "1px solid #000",
                            borderRight: "1px solid #000",
                            borderBottom: "1px solid #000",
                            backgroundColor: "rgba(255,255,255,0.7)",
                          }}
                        />
                      </div>
                      <div style={{ flexShrink: 0, paddingTop: "6px" }}>
                        <img
                          src={`${publicUrl}/FooterAce.png`}
                          alt="Footer"
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
              borderTop: "4px double #000",
              paddingTop: "8px",

                          }}
                          crossOrigin="anonymous"
                        />
                        <div
                          style={{
                            textAlign: "center",
                            marginTop: "4px",
                            fontFamily: '"Times New Roman", "Times", serif',
                          }}
                        >
                          <div
                            style={{
                              fontStyle: "italic",
                              display: "inline-flex",
                              alignItems: "baseline",
                              gap: "6px",
                              fontSize: "12px",
                              fontWeight: "bold",
                              direction: "ltr",
                            }}
                          >
                            <span>Page</span>
                            <strong style={{ fontSize: "13px" }}>
                              {chunkIdx + 1} / {allChunks.length}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </div>
      </PdfPreviewModal>
    </div>
  );
};

export default LoansReport;
